import { useEffect, useMemo, useState } from "react";
import {
  ConfirmDialog,
  ExtendedCard,
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
} from "@NP-Webber/crm-kit";
import { toClientApiError } from "../../api/httpClient";
import {
  usersApi,
  type RoleOption,
  type TenantUser,
} from "../../api/usersApi";
import {
  BadgeOutlinedIcon,
  BlockOutlinedIcon,
  SecurityOutlinedIcon,
} from "../../ui/appIcons";
import { MembershipPermissionsPanel } from "../settings/MembershipPermissionsDialog";

function roleLabel(user: TenantUser): string {
  if (user.roles.length === 0) {
    return "—";
  }
  return user.roles.map((role) => role.name).join(", ");
}

export type UserCardProps = {
  open: boolean;
  user: TenantUser | null;
  roleOptions: RoleOption[];
  /** When set (platform admin viewing another tenant), pass to mutate APIs. */
  scopeTenantId?: string;
  onClose: () => void;
  onChanged?: () => void;
};

export function UserCard({
  open,
  user,
  roleOptions,
  scopeTenantId,
  onClose,
  onChanged,
}: UserCardProps) {
  const [activeUser, setActiveUser] = useState<TenantUser | null>(user);
  const [actionError, setActionError] = useState<string | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableBusy, setDisableBusy] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleBusy, setRoleBusy] = useState(false);
  const [nextRoleKey, setNextRoleKey] = useState("general_user");

  useEffect(() => {
    if (open && user) {
      setActiveUser(user);
      setActionError(null);
    }
    if (!open) {
      setDisableOpen(false);
      setRoleOpen(false);
      setActionError(null);
    }
  }, [open, user]);

  const detailsFields = useMemo(
    () => [
      { key: "displayName", label: "שם", value: activeUser?.displayName ?? "" },
      { key: "email", label: "אימייל", value: activeUser?.email ?? "" },
      {
        key: "role",
        label: "תפקיד",
        value: activeUser ? roleLabel(activeUser) : "—",
      },
      {
        key: "membershipStatus",
        label: "סטטוס חברות",
        value:
          activeUser?.membershipStatus === "active" ? "פעיל" : "מושבת",
      },
    ],
    [activeUser],
  );

  const headerActions = useMemo(() => {
    if (!activeUser) {
      return [];
    }
    if (activeUser.membershipStatus !== "active") {
      return [
        {
          icon: <BadgeOutlinedIcon />,
          tooltip: "הפעלה מחדש",
          onClick: () => {
            void (async () => {
              setDisableBusy(true);
              setActionError(null);
              try {
                const updated = await usersApi.enable(
                  activeUser.membershipId,
                  scopeTenantId,
                );
                setActiveUser(updated);
                onChanged?.();
              } catch (err) {
                setActionError(toClientApiError(err).message);
              } finally {
                setDisableBusy(false);
              }
            })();
          },
        },
      ];
    }
    return [
      {
        icon: <BadgeOutlinedIcon />,
        tooltip: "שינוי תפקיד",
        onClick: () => {
          setNextRoleKey(
            activeUser.roles[0]?.key ?? roleOptions[0]?.key ?? "general_user",
          );
          setRoleOpen(true);
        },
      },
      {
        icon: <BlockOutlinedIcon />,
        tooltip: "השבתה",
        onClick: () => setDisableOpen(true),
      },
    ];
  }, [activeUser, roleOptions, scopeTenantId, onChanged]);

  const handleDisable = async () => {
    if (!activeUser) {
      return;
    }
    setDisableBusy(true);
    setActionError(null);
    try {
      const updated = await usersApi.disable(activeUser.membershipId, scopeTenantId);
      setActiveUser(updated);
      setDisableOpen(false);
      onChanged?.();
    } catch (err) {
      setActionError(toClientApiError(err).message);
      setDisableOpen(false);
    } finally {
      setDisableBusy(false);
    }
  };

  const handleAssignRole = async () => {
    if (!activeUser) {
      return;
    }
    setRoleBusy(true);
    setActionError(null);
    try {
      const updated = await usersApi.assignRole(
        activeUser.membershipId,
        nextRoleKey,
        scopeTenantId,
      );
      setActiveUser(updated);
      setRoleOpen(false);
      onChanged?.();
    } catch (err) {
      setActionError(toClientApiError(err).message);
      setRoleOpen(false);
    } finally {
      setRoleBusy(false);
    }
  };

  if (!open || !activeUser) {
    return null;
  }

  return (
    <>
      <ExtendedCard
        key={activeUser.membershipId}
        open
        onClose={onClose}
        title={activeUser.displayName}
        subtitle={activeUser.email}
        detailsFields={detailsFields}
        headerActions={headerActions}
        detailsHeaderExtra={
          actionError ? <FormAlert severity="error">{actionError}</FormAlert> : null
        }
        feedSources={{ activities: [], tasks: [], notes: [] }}
        hideFeedTab
        showDashboard={false}
        defaultTab={0}
        extraTabs={[
          {
            label: "הרשאות",
            icon: <SecurityOutlinedIcon />,
            content: (
              <MembershipPermissionsPanel
                membershipId={activeUser.membershipId}
                active={open}
              />
            ),
          },
        ]}
      />

      <FormDialog
        open={roleOpen}
        onClose={() => {
          if (!roleBusy) {
            setRoleOpen(false);
          }
        }}
        size="sm"
      >
        <FormCard
          title="שינוי תפקיד"
          onSave={() => {
            void handleAssignRole();
          }}
          onCancel={() => setRoleOpen(false)}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={roleBusy}
        >
          <FormSection>
            <FormGrid cols={1}>
              <FormField
                label="תפקיד"
                type="select"
                value={nextRoleKey}
                onChange={setNextRoleKey}
                disabled={roleBusy}
                options={roleOptions.map((option) => ({
                  value: option.key,
                  label: option.name,
                }))}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <ConfirmDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        title="השבתת משתמש"
        message={`להשבית את החברות של "${activeUser.displayName}" בארגון זה?`}
        confirmText="השבתה"
        cancelText="ביטול"
        loading={disableBusy}
        onConfirm={() => {
          void handleDisable();
        }}
        onCancel={() => setDisableOpen(false)}
      />
    </>
  );
}
